import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { usePendingValidation } from '@/stores/usePendingValidation';
import type TimeSheetModel from '@/models/entities/TimeSheetModel';
import { TIME_SHEET_SIGNATURE_TYPE } from '@/models/references/references';
import { AbsenceDialog, RejectDialog, ValidateDialog, type SheetSummary } from './ValidationDialogs';

/** Résumé d'une feuille pour ses dialogues (monthLabel : celui de sa période ; signAs : cf. reviewActions) */
export function toSheetSummary(
  sheet: TimeSheetModel,
  monthLabel: string,
  signAs: SheetSummary['signAs'] = TIME_SHEET_SIGNATURE_TYPE.DIRECTOR,
): SheetSummary {
  return {
    urn: sheet.urn,
    memberId: sheet.memberId,
    periodId: Number(sheet.period.id),
    fullName: sheet.fullName,
    monthLabel,
    totalMinutes: sheet.totalMinutes,
    isAbsence: sheet.isAbsence,
    signAs,
  };
}

export type ValidationAction = 'validate' | 'reject' | 'absence';

/** Message une fois l'action faite */
const DONE_MESSAGES: Record<ValidationAction, (sheet: SheetSummary, signed: boolean) => string> = {
  validate: ({ fullName, signAs }) =>
    signAs === TIME_SHEET_SIGNATURE_TYPE.MANAGER
      ? `La feuille de ${fullName} est validée : elle passe au Dirigeant.`
      : `La feuille de ${fullName} est validée.`,
  reject: ({ fullName, isAbsence }) =>
    isAbsence
      ? `L'absence de ${fullName} est annulée : sa feuille repart en brouillon.`
      : `La feuille de ${fullName} est refusée : elle repart en brouillon.`,
  absence: ({ fullName, signAs }, signed) =>
    !signed
      ? `Absence de ${fullName} déclarée : elle est à valider.`
      : signAs === TIME_SHEET_SIGNATURE_TYPE.MANAGER
        ? `Absence de ${fullName} déclarée et signée : elle passe au Dirigeant.`
        : `Absence de ${fullName} déclarée et validée.`,
};

/**
 * Dialogues de la Validation, pour sa liste comme pour le détail d'une feuille : open(action, feuille) ouvre le bon
 * dialogue ; une fois l'action faite, le message, la fermeture, puis onDone (recharger). dialog : à placer dans la page.
 */
export function useValidationDialogs(onDone: () => void): {
  open: (action: ValidationAction, sheet: SheetSummary) => void;
  dialog: ReactNode;
} {
  const [current, setCurrent] = useState<{ action: ValidationAction; sheet: SheetSummary } | null>(null);
  const close = () => setCurrent(null);
  let dialog: ReactNode = null;
  if (current) {
    const { action, sheet } = current;
    const Dialog = action === 'validate' ? ValidateDialog : action === 'reject' ? RejectDialog : AbsenceDialog;
    const done = (signed = false, signError?: string) => {
      // Absence déclarée mais signature refusée (deux requêtes) : elle reste à valider
      if (signError) {
        toast.warning(
          `Absence de ${sheet.fullName} déclarée, mais pas signée : ${signError} Elle reste à valider depuis la Validation.`,
        );
      } else {
        toast.success(DONE_MESSAGES[action](sheet, signed));
      }
      close();
      onDone();
      // Pastille de Validation du manager
      void usePendingValidation.getState().refresh();
    };
    dialog = <Dialog sheet={sheet} onDone={done} onClose={close} />;
  }

  return { open: (action, sheet) => setCurrent({ action, sheet }), dialog };
}
